import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Box,
    Typography,
    Grid,
    TextField,
    Paper,
    Divider,
    Stepper,
    Step,
    StepLabel,
    StepButton,
    Button,
    Chip,
    Snackbar,
    Alert,
} from "@mui/material";
import {
    CheckCircle as CheckCircleIcon,
    Person as PersonIcon,
    Work as WorkIcon,
    Paid as PaidIcon,
    AccountBalance as AccountBalanceIcon,
    Description as DescriptionIcon,
    Policy as PolicyIcon,
} from '@mui/icons-material';
import { postData } from '../../../../services/axiosInstance';
import { API_ENDPOINTS } from '../../../../services/endpoints';

const REHIRE_VERIFICATION_STEPS = [
    {
        key: 'personalContact',
        label: 'Personal & Contact',
        shortLabel: 'Personal',
        icon: <PersonIcon fontSize="small" />,
        tabIndex: 0,
    },
    {
        key: 'employment',
        label: 'Employment Details',
        shortLabel: 'Employment',
        icon: <WorkIcon fontSize="small" />,
        tabIndex: 1,
    },
    {
        key: 'compensationPay',
        label: 'Compensation & Pay',
        shortLabel: 'Compensation',
        icon: <PaidIcon fontSize="small" />,
        tabIndex: 2,
    },
    {
        key: 'bankingStatutory',
        label: 'Banking & Statutory',
        shortLabel: 'Banking',
        icon: <AccountBalanceIcon fontSize="small" />,
        tabIndex: 3,
    },
    {
        key: 'documents',
        label: 'Documents & Other',
        shortLabel: 'Documents',
        icon: <DescriptionIcon fontSize="small" />,
        tabIndex: 4,
    },
    {
        key: 'assignPolicies',
        label: 'Assign Policies',
        shortLabel: 'Policies',
        icon: <PolicyIcon fontSize="small" />,
        tabIndex: 5,
    },
];

function EmployeeRehire({ initialData, rehireChecklist = {}, onUpdateRehireChecklist, onStartRehireVerification, onRehireSuccess }) {
    const [activeStep, setActiveStep] = useState(0);
    const [rehireDetails, setRehireDetails] = useState({
        rehireDate: initialData?.rehire_date || '',
        rehireReason: initialData?.rehire_reason || ''
    });
    const [fieldErrors, setFieldErrors] = useState({
        rehireDate: false,
        rehireReason: false,
    });
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [searchParams] = useSearchParams();
    const employeeIdFromUrl = searchParams.get('employeeId');
    const userIdForPayload = initialData?.user_id || initialData?.id || (employeeIdFromUrl ? Number(employeeIdFromUrl) : undefined);

    const checklist = {
        personalContact: false,
        employment: false,
        compensationPay: false,
        bankingStatutory: false,
        documents: false,
        assignPolicies: false,
        ...rehireChecklist,
    };

    const handleRehireChange = (event) => {
        const { name, value } = event.target;
        setRehireDetails(prev => ({
            ...prev,
            [name]: value
        }));
        setFieldErrors(prev => ({
            ...prev,
            [name]: false,
        }));
    };

    const handleStepClick = (index) => {
        setActiveStep(index);
    };

    const handleGoToVerification = (index) => {
        setActiveStep(index);
        const step = REHIRE_VERIFICATION_STEPS[index];
        if (step && onStartRehireVerification) {
            onStartRehireVerification(step.key, step.label);
        }
    };

    const handleSkip = (stepKey, stepLabel) => {
        const updatedChecklist = {
            ...checklist,
            [stepKey]: true,
        };

        if (onUpdateRehireChecklist) {
            onUpdateRehireChecklist(updatedChecklist);
        }

        setSnackbar({
            open: true,
            message: `${stepLabel} marked complete via Skip`,
            severity: 'info',
        });
    };

    const handleSubmit = async () => {
        const errors = {
            rehireDate: !rehireDetails.rehireDate,
            rehireReason: !rehireDetails.rehireReason,
        };

        if (errors.rehireDate || errors.rehireReason) {
            setFieldErrors(errors);
            setSnackbar({
                open: true,
                message: 'Please fill the required Rehire Date and Reason fields.',
                severity: 'error'
            });
            return;
        }

        const allStepsComplete = REHIRE_VERIFICATION_STEPS.every(step => checklist[step.key]);

        if (!allStepsComplete) {
            setSnackbar({
                open: true,
                message: 'Please complete all verification steps before confirming rehire.',
                severity: 'error'
            });
            return;
        }

        if (!userIdForPayload) {
            setSnackbar({
                open: true,
                message: 'Employee ID is missing. Cannot submit rehire.',
                severity: 'error'
            });
            return;
        }

        const payload = {
            rehire_date: rehireDetails.rehireDate,
            rehire_reason: rehireDetails.rehireReason,
            user_id: Number(userIdForPayload),
        };

        setIsSubmitting(true);

        try {
            const response = await postData(API_ENDPOINTS.reactivateEmployee, payload);
            const success = response?.status === 'SUCCESS' || response?.status === 'success' || response?.statusCode === 200 || response?.statusCode === '200';

            if (success) {
                const successMessage = response?.message || 'Rehire completed successfully.';
                setSnackbar({ open: true, message: successMessage, severity: 'success' });

                if (onUpdateRehireChecklist) {
                    const completedChecklist = REHIRE_VERIFICATION_STEPS.reduce((acc, step) => ({
                        ...acc,
                        [step.key]: true,
                    }), {});
                    onUpdateRehireChecklist(completedChecklist);
                }

                if (onRehireSuccess) {
                    setTimeout(() => {
                        onRehireSuccess();
                    }, 1500);
                }
            } else {
                setSnackbar({
                    open: true,
                    message: response?.message || 'Unable to submit rehire. Please try again.',
                    severity: 'error'
                });
            }
        } catch (error) {
            const message = error?.response?.data?.message || error?.message || 'Unable to complete rehire request.';
            setSnackbar({ open: true, message, severity: 'error' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    return (
        <Box>
            <Typography variant="h6" fontWeight={600} component="h2" gutterBottom sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
                Employee Exit Details (Read Only)
            </Typography>

            {/* EXIT DETAILS SECTION (READ ONLY) */}
            <Paper elevation={2} sx={{ p: 3, borderLeft: '4px solid', borderColor: 'error.main', bgcolor: 'grey.50' }}>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Exit Type"
                            disabled
                            value={initialData?.type || 'N/A'}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Final Date of Exit"
                            disabled
                            value={initialData?.final_date_of_exit || 'N/A'}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            fullWidth
                            label="Exit Reason"
                            disabled
                            value={initialData?.reason_for_exit || 'N/A'}
                            InputLabelProps={{ shrink: true }}
                            multiline
                            rows={3}
                        />
                    </Grid>
                </Grid>
            </Paper>

            <Box sx={{ mt: 4 }}>
                <Typography variant="h6" gutterBottom color="success.main" fontWeight={600} sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
                    1. Rehire Status & Details
                </Typography>

                {/* REHIRE FORM SECTION (EDITABLE) */}
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            fullWidth
                            label="Rehire Date"
                            type="date"
                            name="rehireDate"
                            value={rehireDetails.rehireDate}
                            onChange={handleRehireChange}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                            error={fieldErrors.rehireDate}
                            helperText={fieldErrors.rehireDate ? 'Rehire Date is required.' : 'Employee status becomes ACTIVE from this date.'}
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <TextField
                            fullWidth
                            label="Reason for Rehire"
                            name="rehireReason"
                            value={rehireDetails.rehireReason}
                            onChange={handleRehireChange}
                            multiline
                            rows={3}
                            error={fieldErrors.rehireReason}
                            helperText={fieldErrors.rehireReason ? 'Reason for Rehire is required.' : 'Justify the decision to rehire.'}
                            size="small"
                        />
                    </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                <Typography variant="h6" gutterBottom color="primary" fontWeight={600} sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
                    2. Rehire Data Verification Steps
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Click on each step below to navigate to the respective tab and complete verification.
                </Typography>

                <Stepper activeStep={activeStep} alternativeLabel>
                    {REHIRE_VERIFICATION_STEPS.map((step, index) => (
                        <Step key={step.key} completed={checklist[step.key]}>
                            <StepButton onClick={() => handleStepClick(index)}>
                                <StepLabel
                                    StepIconComponent={() => (
                                        <Box
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: '50%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                bgcolor: checklist[step.key]
                                                    ? 'success.main'
                                                    : activeStep === index
                                                        ? 'primary.main'
                                                        : 'grey.300',
                                                color: 'white',
                                                transition: 'all 0.3s',
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    transform: 'scale(1.1)',
                                                }
                                            }}
                                        >
                                            {checklist[step.key] ? (
                                                <CheckCircleIcon />
                                            ) : (
                                                step.icon
                                            )}
                                        </Box>
                                    )}
                                >
                                    <Typography variant="caption" sx={{ mt: 1, fontWeight: 500 }}>
                                        Step {index + 1}
                                    </Typography>
                                    <Typography variant="caption" display="block" sx={{ fontSize: '0.7rem' }}>
                                        {step.shortLabel}
                                    </Typography>
                                </StepLabel>
                            </StepButton>
                        </Step>
                    ))}
                </Stepper>

                <Grid container spacing={2} mt={4}>
                    {REHIRE_VERIFICATION_STEPS.map((step, index) => (
                        <Grid size={{ xs: 12, sm: 6 }} key={step.key}>
                            <Paper
                                elevation={1}
                                sx={{
                                    p: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    border: '2px solid',
                                    borderColor: checklist[step.key] ? 'success.light' : 'grey.300',
                                    borderRadius: 2,
                                    transition: 'all 0.3s',
                                }}
                            >
                                <Box sx={{ mr: 2, color: checklist[step.key] ? 'success.main' : 'text.secondary' }}>
                                    {step.icon}
                                </Box>
                                <Box sx={{ flexGrow: 1 }}>
                                    <Typography variant="body2" fontWeight={500}>
                                        Step {index + 1}: {step.label}
                                    </Typography>
                                </Box>
                                <Chip
                                    label={checklist[step.key] ? 'Completed' : 'Pending'}
                                    color={checklist[step.key] ? 'success' : 'warning'}
                                    size="small"
                                    sx={{ mr: 1 }}
                                />

                                {!checklist[step.key] && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="warning"
                                        sx={{ mr: 1, textTransform: 'none' }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleSkip(step.key, step.label);
                                        }}
                                    >
                                        Skip
                                    </Button>
                                )}

                                <Button
                                    size="small"
                                    variant={checklist[step.key] ? 'outlined' : 'contained'}
                                    color={checklist[step.key] ? 'secondary' : 'primary'}
                                    onClick={() => handleGoToVerification(index)}
                                    sx={{ textTransform: 'none' }}
                                >
                                    {checklist[step.key] ? 'Review' : 'Verify'}
                                </Button>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>

                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        sx={{ textTransform: 'none' }}
                    >
                        Confirm Rehire
                    </Button>
                </Box>
            </Box>
            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default EmployeeRehire;